import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Item } from '../../model/item';
import { Answer } from '../../model/answer';
import { Token } from "../../model/token";
import { WelcomeDTO } from "../../model/welcome.info";
import { ConfigurationControlService } from "../configuration/configurationControl.service";
import { CONFIG } from '../../constants/constants';
import { Session } from '../../model/session';
import { DateServerService } from '../dateServerService/date-server.service';
import { catchError, timeout } from 'rxjs/operators';
import { throwError, Observable } from 'rxjs';

//Servicio para Login
@Injectable({
  providedIn: 'root'
})
export class LoginService {

  //objeto para accceso a base de datos indexedDB
  private dataBase: any = null;

  //constructor de la clase
  constructor(private http: HttpClient,
    private _configurationControlService: ConfigurationControlService,
    private _dateServerService: DateServerService) {
    this.http = http;
  }

  /*Metodo principla de login, consume un servicio de plexi-rest
    al cual pasa un usuario y un password y cuando tiene respuesta
    resuelve una promesa con un objeto que copntiene el token JWE
    devuelto por plexi-rest entre otra informacion de la cuenta*/
  public login(user: string, password: string, ip: string): Promise<Token> {
    let _headers = new HttpHeaders({
      'Content-Type': 'application/json',
      'Authorization': CONFIG.PLX_AUTH_LOGIN,
      'usuario': user,
      'password': password,
      'ip': ip,
      'fecha': this._dateServerService.getDateServer().getTime().toString()
    });

    return this.http.get<Token>(CONFIG.REST_END_POINT + 'autenticate/', { headers: _headers }).toPromise();
  }


  /*Se encarga de consultar la informacion de la pagina de bienvenida,
  consume un servicio de plexi-rest al cual pasa el nombre de usuario
  que inicia sesion, cuando tiene respuesta, resuelve una promesa con un
  objeto que contiene toda la informacion de la pagina de bienvenida*/
  public async loadWelcomeInfo(userId: number, token: string, userName: string, sesionMultiple: Boolean): Promise<WelcomeDTO> {
    let _headers = new HttpHeaders({
      'Content-Type': 'application/json',
      'token': token,
      'user': userId.toString(),
      'userName': userName,
      'sesionMultiple': sesionMultiple.toString()
    });

    const response = await this.http.get<WelcomeDTO>(CONFIG.REST_END_POINT + 'loadWelcomeInfo/', { headers: _headers }).toPromise();

    // Asegurar que sessionInfo se cargue globalmente
    if (response && (response as any).sessionInfo) {
      this._configurationControlService.sessionInfo = (response as any).sessionInfo;
    }

    return response;
  }


  /*Se encarga de guardar un token en el store en indexedDB*/
  public saveToken(token: Token) {
    let self = this;
    if (self.dataBase) {
      return;
    }
    self.dataBase = indexedDB.open(CONFIG.DB_NAME);
    self.dataBase.onupgradeneeded = function (event: any) {
      let conection = self.dataBase.result;
      let itemsStore = conection.createObjectStore(
        CONFIG.ITEMS_STORE,
        { keyPath: 'itemId', autoIncrement: false }
      );
      itemsStore.createIndex(
        'by_order',
        ['order', 'sesion'],
        { unique: true }
      );
      let answerStore =
        conection.createObjectStore(
          CONFIG.ANSWER_STORE,
          { keyPath: 'respuestaId', autoIncrement: true }
        );
      answerStore.createIndex(
        'by_synchronized',
        CONFIG.SYNC_ANSWER_INDEX,
        { unique: false }
      );
      answerStore.createIndex('by_answerId', 'itemId', { unique: false });
      answerStore.createIndex(
        'by_max_date',
        ['fechaRespuesta', 'itemId'],
        { unique: false }
      );
      let tokenStore = conection.createObjectStore(
        CONFIG.TOKEN_STORE,
        { keyPath: 'tokenId', autoIncrement: false }
      );
      tokenStore.createIndex('by_tokenId', 'tokenId', { unique: true });
      let stateStore =
        conection.createObjectStore(
          CONFIG.STATE_STORE,
          { keyPath: 'consecutive', autoIncrement: false }
        );
      stateStore.createIndex('by_consecutive', 'consecutive', { unique: true });
    }
    self.dataBase.onsuccess = function (event: any) {
      let connection = self.dataBase.result;
      let transaction = connection.transaction([CONFIG.TOKEN_STORE], 'readwrite');
      let objStore = transaction.objectStore(CONFIG.TOKEN_STORE);
      let request = objStore.put(token);
      request.onerror = function (event: any) {
        console.log('...Error insertando el token');
      }
      transaction.oncomplete = function (event: any) { }
    }
    self.dataBase.onerror = function (event: any) {
      console.log('...error opening dataBase...');
    }
  }

  /*Se encargade cambiar el estado de una sesion*/
  public changeSessionState(token: string,
    user: string,
    credential: string,
    estadoSesion: number,
    sessionInfo: Session): Promise<any> {
    let self = this;

    if (token) {
      let _headers = new HttpHeaders({
        'Content-Type': 'application/json',
        'user': user,
        'token': token,
        'credential': credential,
        'estadoSesion': estadoSesion.toString()
      });
      const options: {
        headers?: HttpHeaders,
        observe?: 'body',
        params?: HttpParams,
        reportProgress?: boolean,
        responseType: 'text',
        withCredentials?: boolean
      } = {
        headers: _headers,
        responseType: 'text'
      };

      const timeoutDuration = this._configurationControlService.timeoutService; // Establecer el tiempo de espera en milisegundos
      return self.http.post(CONFIG.REST_END_POINT + 'changeSessionState/', sessionInfo, options)
        .pipe(
          timeout(timeoutDuration),
          catchError(error => {
            if (error.name === 'TimeoutError') {
              // Manejar el error de tiempo de espera
              console.log('La solicitud excedió el tiempo de espera.');
            }
            return throwError(error);
          })
        ).toPromise();

    }
  }

  public saveSessionInfo(token: string, user: string, state: number): Promise<string> {
    let self = this;
    self._configurationControlService.sessionInfo.estado = state;
    self._configurationControlService.sessionInfo.fechaIngreso = this._dateServerService.getDateServer();
    if (token) {
      let _headers = new HttpHeaders({
        'Content-Type': 'application/json',
        'token': token,
        'user': user
      });
      const options: {
        headers?: HttpHeaders,
        observe?: 'body',
        params?: HttpParams,
        reportProgress?: boolean,
        responseType: 'text',
        withCredentials?: boolean
      } = {
        headers: _headers,
        responseType: 'text'
      };
      return this.http.post(CONFIG.REST_END_POINT + 'saveSessionInfo/', self._configurationControlService.sessionInfo,
        options)
        .toPromise();
    } else {
      console.log('error from service');
    }
  }

  /** Encargado de capturar Ip publica del cliente */
  // getPublicIp(): Observable<{ ip: string }> {
  //   return this.http.get<{ ip: string }>('https://api.ipify.org?format=json');
  // }

  public getIp(): Promise<any> {
    // return this.http.get("https://api64.ipify.org?format=json").toPromise();
    return this.http.get(CONFIG.REST_END_POINT + 'ip/').toPromise();
  }

  /**Método encargado de verificar listas blancas */
  public verifyWhiteList(infoWhiteList): Promise<any> {
    let self = this;
    let _headers = new HttpHeaders({
      'Content-Type': 'application/json'
    });
    const options: {
      headers?: HttpHeaders,
      observe?: 'body',
      params?: HttpParams,
      reportProgress?: boolean,
      responseType: 'text',
      withCredentials?: boolean
    } = {
      headers: _headers,
      responseType: 'text'
    };
    return this.http.post(CONFIG.REST_END_POINT + 'verifyWhiteList/', JSON.stringify(infoWhiteList), options).toPromise();
  }

  public registrarJuramento(token: string, user: string, ordenJornada: number): Promise<any> {
    let self = this;
    let _headers = new HttpHeaders({
      'Content-Type': 'application/json',
      'token': token,
      'user': user,
      'ordenJornada': ordenJornada.toString()
    });
    const options: {
      headers?: HttpHeaders,
      observe?: 'body',
      params?: HttpParams,
      reportProgress?: boolean,
      responseType: 'text',
      withCredentials?: boolean
    } = {
      headers: _headers,
      responseType: 'text'
    };

    const sessionInfo = self._configurationControlService.sessionInfo;

    const timeoutDuration = this._configurationControlService.timeoutService; // Establecer el tiempo de espera en milisegundos

    return this.http.post(CONFIG.REST_END_POINT + 'registarJuramento/',
      sessionInfo,
      options)
      .pipe(
        timeout(timeoutDuration),
        catchError(error => {
          if (error.name === 'TimeoutError') {
            // Manejar el error de tiempo de espera
            console.log('La solicitud excedió el tiempo de espera.');
          }
          return throwError(error);
        })
      ).toPromise();
  }


}
